"use client";

import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import * as React from "react";

import { Button } from "./Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./Command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./dropdown-menu";

export type SideDropdownOption = {
  value: string;
  label: string;
  subOptions?: Array<{ value: string; label: string }>;
};

export function ComboboxSideDropdown({
  options,
  placeholder = "Selectează...",
  valueParent,
  valueChild,
  onChange,
  buttonClassName,
  open: controlledOpen,
  onOpenChange,
  disabled,
}: {
  options: SideDropdownOption[];
  placeholder?: string;
  valueParent?: string; // selected parent id
  valueChild?: string; // selected child id
  onChange: (parent: string, child: string) => void;
  buttonClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isOpenControlled = typeof controlledOpen === "boolean";
  const open = isOpenControlled ? (controlledOpen as boolean) : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isOpenControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const currentParent = options.find((o) => o.value === valueParent);
  const currentChildLabel = currentParent?.subOptions?.find(
    (s) => s.value === valueChild
  )?.label;
  const buttonLabel = currentChildLabel
    ? currentChildLabel
    : currentParent?.label || placeholder;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={"w-full justify-between " + (buttonClassName || "")}
          disabled={disabled}
        >
          {buttonLabel}
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[260px] p-0">
        <DropdownMenuLabel className="px-3 py-2 text-xs text-muted-foreground">
          {placeholder}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {options.map((opt) => {
            const hasSubs = (opt.subOptions || []).length > 0;
            if (!hasSubs) {
              return (
                <DropdownMenuItem
                  key={opt.value}
                  className="flex items-center gap-2"
                  onSelect={() => {
                    onChange(opt.value, "");
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={
                      "h-4 w-4 " +
                      (valueParent === opt.value && !valueChild
                        ? "opacity-100"
                        : "opacity-0")
                    }
                  />
                  {opt.label}
                </DropdownMenuItem>
              );
            }
            return (
              <DropdownMenuSub key={opt.value}>
                <DropdownMenuSubTrigger className="flex items-center gap-2">
                  <CheckIcon
                    className={
                      "h-4 w-4 " +
                      (valueParent === opt.value ? "opacity-100" : "opacity-0")
                    }
                  />
                  {opt.label}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="p-0 w-[260px]">
                  <Command>
                    <CommandInput placeholder="Caută..." className="h-9" />
                    <CommandList>
                      <CommandEmpty>Fără rezultate</CommandEmpty>
                      <CommandGroup>
                        {(opt.subOptions || []).map((s) => (
                          <CommandItem
                            key={s.value}
                            value={s.label}
                            onSelect={() => {
                              onChange(opt.value, s.value);
                              setOpen(false);
                            }}
                          >
                            <CheckIcon
                              className={
                                "mr-2 h-4 w-4 " +
                                (valueParent === opt.value &&
                                valueChild === s.value
                                  ? "opacity-100"
                                  : "opacity-0")
                              }
                            />
                            {s.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            );
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
